export const checkQuestionVisibility = (question, answers, allQuestions = [], parentRepeatableKey = null) => {
  if (!question) return false;

  // Check if this is a nested question with parentAnswerValue
  if (question.parentAnswerValue !== null && question.parentAnswerValue !== undefined) {
    if (question.parentId) {
      // Find parent question
      const parentQuestion = allQuestions.find(
        (q) => 
          (q._id && q._id.toString() === question.parentId.toString()) ||
          (q._tempId && q._tempId === question.parentId) ||
          (q.key === question.parentId) // Fallback
      );
      
      if (parentQuestion) {
        // If parent is repeatable, check if any item matches parentAnswerValue
        if (parentQuestion.repeatable && !parentQuestion.parentId) {
          const repeatableValue = answers[parentQuestion.key];
          if (!Array.isArray(repeatableValue) || repeatableValue.length === 0) {
            // No items added yet, hide nested questions
            return false;
          }
          // Check if any item matches parentAnswerValue
          const hasMatchingItem = repeatableValue.some(itemValue => itemValue === question.parentAnswerValue);
          return hasMatchingItem;
        } else {
          // Non-repeatable parent: check if current answer matches parentAnswerValue
          const currentAnswer = answers[parentQuestion.key];
          return currentAnswer === question.parentAnswerValue;
        }
      }
      // Parent not found, hide question
      return false;
    }
  }

  // LocationSelector dependsOn: dependsOn is a question ID (_id or _tempId)
  // Need to find the question by ID, then use its key to check answers
  if (question.dependsOn) {
    // Try to find parent question by _id or _tempId
    const parentQuestion = allQuestions.find(
      (q) => 
        (q._id && q._id.toString() === question.dependsOn.toString()) ||
        (q._tempId && q._tempId === question.dependsOn) ||
        (q.key === question.dependsOn) // Fallback: might be key in some cases
    );
    
    if (parentQuestion) {
      // Use parent question's key to check answer
      const dependsOnAnswer = answers[parentQuestion.key];
      if (!dependsOnAnswer || dependsOnAnswer === '') {
        return false;
      }
      
      // For LocationSelector: also check if parent has a valid location value
      if (parentQuestion.type === 'LocationSelector' && parentQuestion.source) {
        try {
          const parsedAnswer = typeof dependsOnAnswer === 'string' 
            ? JSON.parse(dependsOnAnswer) 
            : dependsOnAnswer;
          
          if (parentQuestion.source === 'il') {
            if (!parsedAnswer?.il?.code) return false;
          } else if (parentQuestion.source === 'ilce') {
            if (!parsedAnswer?.ilce?.code) return false;
          }
        } catch (e) {
          // Invalid JSON, hide question
          return false;
        }
      }
    } else {
      // Parent question not found, hide this question
      return false;
    }
  }

  return true;
};

export const getVisibleQuestionsFlat = (questions, answers = {}, collected = [], parentRepeatableKey = null, allQuestions = []) => {
  if (!questions || !Array.isArray(questions)) {
    return collected;
  }

  questions.forEach((q) => {
    if (!q || !q.isActive) {
      return;
    }

    if (!checkQuestionVisibility(q, answers, allQuestions, parentRepeatableKey)) {
      return;
    }

    collected.push(q);

    if (q.repeatable && !q.parentId) {
      const repeatableKey = q.key;
      const repeatableValue = answers[repeatableKey];
      
      if (Array.isArray(repeatableValue) && repeatableValue.length > 0) {
        repeatableValue.forEach((item, itemIndex) => {
          if (q.answers && Array.isArray(q.answers)) {
            q.answers.forEach((answer) => {
              if (answer.content && answer.content.nestedQuestions) {
                const nestedQuestions = answer.content.nestedQuestions;
              getVisibleQuestionsFlat(
                nestedQuestions,
                answers,
                collected,
                `${repeatableKey}_${itemIndex}`,
                allQuestions
              );
              }
            });
          }
        });
      }
    } else {
      if (q.answers && Array.isArray(q.answers)) {
        q.answers.forEach((answer) => {
          if (answer.content && answer.content.nestedQuestions) {
            const nestedQuestions = answer.content.nestedQuestions;
            const currentAnswerValue = answers[q.key];
            
            if (answer.value === currentAnswerValue) {
              getVisibleQuestionsFlat(
                nestedQuestions,
                answers,
                collected,
                parentRepeatableKey,
                allQuestions
              );
            }
          }
        });
      }
    }
  });

  return collected;
};

export const getQuestionsByStep = (questions, answers = {}, allQuestions = []) => {
  if (!questions || !Array.isArray(questions)) {
    return new Map();
  }

  const rootQuestions = questions.filter(
    (q) => q && !q.parentId && q.isActive !== false
  );

  const stepMap = new Map();

  rootQuestions.forEach((q) => {
    if (!checkQuestionVisibility(q, answers, allQuestions, null)) {
      return;
    }

    const step = q.step !== undefined && q.step !== null ? q.step : 0;

    if (!stepMap.has(step)) {
      stepMap.set(step, []);
    }

    stepMap.get(step).push(q);
  });

  stepMap.forEach((questionsInStep, step) => {
    questionsInStep.sort((a, b) => {
      const orderA = a.order !== undefined && a.order !== null ? a.order : 0;
      const orderB = b.order !== undefined && b.order !== null ? b.order : 0;
      return orderA - orderB;
    });
  });

  return stepMap;
};

export const getSortedSteps = (stepMap) => {
  return Array.from(stepMap.keys()).sort((a, b) => a - b);
};

export const getQuestionsForStep = (questions, step, answers = {}, allQuestions = []) => {
  const stepMap = getQuestionsByStep(questions, answers, allQuestions);
  return stepMap.get(step) || [];
};

/**
 * Get Turkish label for question type
 * @param {string} type - Question type (e.g., "Text", "TextArea", etc.)
 * @returns {string} Turkish label
 */
export const getQuestionTypeLabel = (type) => {
  const typeLabels = {
    'Text': 'Metin',
    'TextArea': 'Çok Satırlı Metin',
    'Number': 'Sayı',
    'Date': 'Tarih',
    'Time': 'Saat',
    'DateTime': 'Tarih ve Saat',
    'RadioButton': 'Radyo Butonu',
    'DropDown': 'Açılır Liste',
    'Checkbox': 'Onay Kutusu',
    'LocationSelector': 'Konum Seçici',
    'Signature': 'İmza',
  };
  
  return typeLabels[type] || type;
};

